import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, ChevronRight, Plus } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { useUtilityTypes } from '@/hooks/use-utilities'
import { useBillingCycles, useGenerationRuns } from '@/hooks/use-billing'
import {
  buildCycles,
  formatBillingPeriod,
  formatRunDate,
  RunStatusBadge,
} from './billing-cycle-shared'
import { NewPeriodModal } from './new-period-modal'

/** Billing cycles for one utility type — Level 1 of the Invoices ▸ utility type ▸ period
 * drill-down. Each row is exactly what the old "Generate Invoices" modal showed. */
export function UtilityCyclesPage({ basePath }: { basePath: string }) {
  const { utilityTypeId } = useParams<{ utilityTypeId: string }>()
  const navigate = useNavigate()
  const { data: utilityData, isLoading: utilityLoading } = useUtilityTypes()
  const { data: runsData } = useGenerationRuns()
  const { data: cyclesData } = useBillingCycles()
  const [newPeriodOpen, setNewPeriodOpen] = useState(false)

  const utilityType = (utilityData?.data ?? []).find((t) => t.id === utilityTypeId)
  const cycles = utilityType
    ? buildCycles(cyclesData?.data ?? [], runsData?.data ?? [], utilityType.id)
    : []

  const backLink = (
    <Link
      to={`${basePath}?tab=by-period`}
      className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Back to billing
    </Link>
  )

  if (utilityLoading) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Loading utility type…</p>
      </PageShell>
    )
  }

  if (!utilityType) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Utility type not found.</p>
        <div className="mt-4">{backLink}</div>
      </PageShell>
    )
  }

  function goToCycle(period: string) {
    navigate(`${basePath}/cycles/${utilityType!.id}/${period}`)
  }

  return (
    <PageShell>
      <PageHeader
        label="Finance"
        title={utilityType.name}
        description={
          utilityType.billingMethod === 'meter_based'
            ? `Meter-based · ${utilityType.frequency}`
            : `Flat fee · ${utilityType.frequency}`
        }
        action={backLink}
      />

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-sans text-label uppercase tracking-[0.1em] text-muted-foreground">
            Billing cycles
          </p>
          <Button size="sm" onClick={() => setNewPeriodOpen(true)}>
            <Plus size={14} className="mr-1.5" />
            New period
          </Button>
        </div>

        {cycles.length === 0 ? (
          <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
            No billing cycles yet for {utilityType.name}.
          </p>
        ) : (
          <div className="overflow-hidden rounded border border-border-2">
            {cycles.map((cycle) => (
              <button
                key={cycle.period}
                type="button"
                onClick={() => goToCycle(cycle.period)}
                className="flex w-full items-center justify-between gap-3 border-b border-border-2 px-4 py-3 text-left last:border-0 hover:bg-cream-2"
              >
                <div>
                  <p className="font-sans text-sm font-medium text-ink">
                    {formatBillingPeriod(cycle.period)}
                  </p>
                  <p className="font-sans text-xs text-muted-foreground">
                    {cycle.latest
                      ? `${cycle.latest.invoicesCreated} invoice(s) · ${cycle.latest.mode} · generated ${formatRunDate(cycle.latest.createdAt)}`
                      : 'No attempts yet'}
                  </p>
                  {cycle.latest && cycle.latest.plotsPendingReading > 0 && (
                    <p className="font-sans text-xs text-muted-foreground">
                      {cycle.latest.plotsPendingReading} plot(s) still need a reading
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <RunStatusBadge run={cycle.latest} />
                  <ChevronRight size={16} className="text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {newPeriodOpen && (
        <NewPeriodModal
          utilityType={utilityType}
          cycles={cycles}
          onClose={() => setNewPeriodOpen(false)}
          onCreate={(period) => goToCycle(period)}
          onOpenExisting={(period) => goToCycle(period)}
        />
      )}
    </PageShell>
  )
}
